import type { Condition, SearchQueryBody } from "@/types";

import { HttpClient, PaginatedResponse, robotoHeaders } from "../../http";
import type { UserRecord } from "../users/UserRecord";

import { OrgRecord, OrganizationTier, RobotoRegion } from "./OrgRecord";

export interface OrgUserRecord {
  user: UserRecord;
  org: OrgRecord;
  roles: string[];
}

interface Options {
  abortSignal: AbortSignal;
  searchParams: URLSearchParams;
}

export class OrgService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async getAvailableDataRegions(
    options?: Partial<Options>,
  ): Promise<RobotoRegion[]> {
    const requestUrl = this.#httpClient.constructUrl(`v1/integrations/regions`);
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<RobotoRegion[]>();
  }

  public async getOrg(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<OrgRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/orgs`);
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({
        orgId: orgId,
      }),
    });
    return response.json<OrgRecord>();
  }

  public async getAllOrgs(options?: Partial<Options>): Promise<OrgRecord[]> {
    const orgs = [];
    let pageToken = null;

    while (true) {
      const searchParams = new URLSearchParams();
      if (pageToken) {
        searchParams.append("page_token", pageToken);
      }

      const url = this.#httpClient.constructUrl("v1/orgs/all", searchParams);
      const response = await this.#httpClient.get(url, {
        signal: options?.abortSignal,
      });

      const paginatedResponse =
        await response.json<PaginatedResponse<OrgRecord>>();
      orgs.push(...paginatedResponse.items);
      pageToken = paginatedResponse.next_token;
      if (!pageToken) {
        break;
      }
    }

    return orgs;
  }

  /**
   * One-page org search for autocompletes. A thin wrapper over
   * {@link listOrgs} that returns only the first page's items.
   */
  public async searchOrgs(
    search: string,
    options?: Partial<Options>,
  ): Promise<OrgRecord[]> {
    const page = await this.listOrgs({ search, limit: 50 }, options);
    return page.items;
  }

  /**
   * Admin-only paginated org search, POSTed to `v1/orgs/search` as a
   * `SearchQueryBody` like every `/search` endpoint. This method is the single
   * place the picker-style params become a structured body: `search` matches
   * `name` or `org_id` as an OR of CONTAINS conditions, `tier` becomes an
   * EQUALS condition AND-ed on, `pageToken` rides in `after`. The backend
   * whitelists condition fields (`org_id`, `name`, `tier`) and `sort_by`
   * (`created`/`name`/`org_id`, default `created` descending), and returns a
   * `total_count` alongside each page.
   */
  public async listOrgs(
    params: {
      search?: string;
      tier?: OrganizationTier;
      pageToken?: string;
      limit?: number;
    },
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<OrgRecord>> {
    const conditions: Condition[] = [];
    if (params.search && params.search.length > 0) {
      conditions.push({
        operator: "OR",
        conditions: [
          { field: "name", comparator: "CONTAINS", value: params.search },
          { field: "org_id", comparator: "CONTAINS", value: params.search },
        ],
      });
    }
    if (params.tier !== undefined) {
      conditions.push({
        field: "tier",
        comparator: "EQUALS",
        value: params.tier,
      });
    }
    let condition: Condition | undefined;
    if (conditions.length === 1) {
      condition = conditions[0];
    } else if (conditions.length > 1) {
      condition = { operator: "AND", conditions };
    }

    const query: SearchQueryBody = {
      ...(condition && { condition }),
      ...(params.pageToken && { after: params.pageToken }),
      ...(params.limit !== undefined && { limit: params.limit }),
    };

    const url = this.#httpClient.constructUrl("v1/orgs/search");
    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(query),
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<OrgRecord>>();
  }

  public async getOrgUsers(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<OrgUserRecord[]> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/users`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<OrgUserRecord[]>();
  }

  /**
   * Structured search over one org's members, paginated.
   *
   * The bounded counterpart to {@link getOrgUsers}, which returns the whole
   * membership — with its org and role rows — in one unpaginated response.
   * Prefer this for member pickers: records carry only `user_id` and `name`
   * (the two fields a picker row renders), and `limit` caps the page so a large
   * org doesn't have to be pulled in full before the list can be shown.
   *
   * POSTs a `SearchQueryBody` to `v1/orgs/id/<org_id>/users/search`, like every
   * `/search` endpoint. The backend whitelists condition fields and `sort_by`
   * to `user_id` and `name` (default `user_id` ascending); a picker's typed
   * term goes in as an OR of two CONTAINS conditions. Omit `condition` for the
   * on-open page. A response with no `next_token` is the complete match set,
   * which is what lets a picker filter it client-side instead of re-querying
   * per keystroke.
   */
  public async searchOrgUsers(
    orgId: string,
    query: SearchQueryBody,
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<UserRecord>> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/users/search`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      body: JSON.stringify(query),
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<UserRecord>>();
  }

  public async updateOrgTier(
    orgId: string,
    tier: OrganizationTier,
    options?: Partial<Options>,
  ): Promise<OrgRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/tier`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      signal: options?.abortSignal,
      body: JSON.stringify({ tier: tier }),
    });
    return response.json<OrgRecord>();
  }
}
